import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import {
  InterviewProblem,
  PROBLEMS,
  PROBLEM_DIFFICULTIES,
  ProblemDifficulty,
  ProblemDifficultyNames,
} from "../../models/problems";
import SaveDesignModal from "../scoreboard/SaveDesignModal";
import { ProblemCard } from "./ProblemCard";
import { useHasUnsavedScratchpad, useRestartProblem, useStartProblem } from "./useStartProblem";
import { useProblemAttempts } from "../../hooks/useProblemAttempts";
import { useAuth } from "../auth/AuthProvider";
import { COLOR_TEXT_MUTED } from "../../styles/colors";

type DifficultyFilter = ProblemDifficulty | "all";

interface PendingStart {
  problem: InterviewProblem;
  restart: boolean;
}

interface ProblemLibraryProps {
  /** Called after the problem has been loaded into the game state. */
  onStarted: (problem: InterviewProblem) => void;
  problems?: InterviewProblem[];
}

export const ProblemLibrary: React.FC<ProblemLibraryProps> = ({ onStarted, problems = PROBLEMS }) => {
  const [filter, setFilter] = useState<DifficultyFilter>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Waiting on the "replace your scratchpad?" confirm
  const [pendingStart, setPendingStart] = useState<PendingStart | null>(null);
  // Waiting on the "discard your saved attempt?" confirm
  const [restartTarget, setRestartTarget] = useState<InterviewProblem | null>(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const hasUnsavedScratchpad = useHasUnsavedScratchpad();
  const startProblem = useStartProblem();
  const restartProblem = useRestartProblem();
  const attempts = useProblemAttempts();
  const { requireAuth } = useAuth();

  const visibleProblems = filter === "all" ? problems : problems.filter((p) => p.difficulty === filter);
  const attemptedCount = problems.filter((p) => attempts[p.id]).length;

  const start = ({ problem, restart }: PendingStart) => {
    setPendingStart(null);
    if (restart) {
      restartProblem(problem.id);
    } else {
      startProblem(problem.id);
    }
    onStarted(problem);
  };

  const requestStart = (pending: PendingStart) => {
    // Practice problems need a (free) account; sign-in comes first, then the start carries on
    requireAuth(
      () => {
        if (hasUnsavedScratchpad) {
          setPendingStart(pending);
        } else {
          start(pending);
        }
      },
      {
        reason: "practice",
        // Replayed after Google sign-in, unless that would skip the "replace your scratchpad?" confirm
        pendingProblem: hasUnsavedScratchpad ? undefined : { problemId: pending.problem.id, restart: pending.restart },
      },
    );
  };

  const handleStartClick = (problem: InterviewProblem) => requestStart({ problem, restart: false });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          size="small"
          onChange={(_e, value: DifficultyFilter | null) => value && setFilter(value)}
          aria-label="Filter by difficulty"
        >
          <ToggleButton value="all" sx={{ textTransform: "none", px: 2 }}>
            All
          </ToggleButton>
          {PROBLEM_DIFFICULTIES.map((difficulty) => (
            <ToggleButton key={difficulty} value={difficulty} sx={{ textTransform: "none", px: 2 }}>
              {ProblemDifficultyNames[difficulty]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_MUTED }} data-testid="attempted-count">
          {attemptedCount} of {problems.length} attempted
        </Typography>
      </Box>

      {visibleProblems.map((problem) => (
        <ProblemCard
          key={problem.id}
          problem={problem}
          expanded={expandedId === problem.id}
          onToggle={() => setExpandedId(expandedId === problem.id ? null : problem.id)}
          onStart={handleStartClick}
          attempt={attempts[problem.id]}
          onRestart={setRestartTarget}
        />
      ))}

      {/* Start over confirmation */}
      <Dialog
        open={restartTarget !== null}
        onClose={() => setRestartTarget(null)}
        aria-labelledby="restart-problem-title"
      >
        <DialogTitle id="restart-problem-title">Start over?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Discard your saved attempt at {restartTarget?.title} and start fresh? This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestartTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            data-testid="confirm-restart-btn"
            onClick={() => {
              const problem = restartTarget;
              setRestartTarget(null);
              if (problem) requestStart({ problem, restart: true });
            }}
          >
            Start over
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved scratchpad confirmation */}
      <Dialog
        open={pendingStart !== null && !isSaveModalOpen}
        onClose={() => setPendingStart(null)}
        aria-labelledby="replace-scratchpad-title"
      >
        <DialogTitle id="replace-scratchpad-title">Replace your scratchpad?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Starting a problem replaces the blocks on your scratchpad. Save it as a design first to keep it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStart(null)}>Cancel</Button>
          <Button color="error" onClick={() => pendingStart && start(pendingStart)} data-testid="discard-and-start-btn">
            Discard and start
          </Button>
          <Button variant="contained" onClick={() => setIsSaveModalOpen(true)} data-testid="save-first-btn">
            Save first
          </Button>
        </DialogActions>
      </Dialog>

      <SaveDesignModal
        open={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSaved={() => {
          setIsSaveModalOpen(false);
          if (pendingStart) start(pendingStart);
        }}
      />
    </Box>
  );
};
